import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Chip, Dialog, Skeleton, Tooltip } from "@mui/material";
import { FC, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  fetchFormResponseErrors,
  selectLensEditorFormFields,
  selectLensEditorLensId,
  selectProfileBuilderFormResponseErrorFieldsUnique,
  selectProfileBuilderFormResponseErrors,
  selectProfileBuilderFormResponseErrorsLoading,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import useCopy from "helpers/hooks/useCopy";
import RequestStatus from "redux/slices/RequestStatus";
import IssueCard from "./IssueCard";
import useAppDispatchWithSnackbar from "../../../helpers/hooks/useAppDispatchWithSnackbar";
import PatternConfetti from "components/PatternConfetti";

type PIDProps = {
  open: boolean;
  onClose: () => void;
};

const ProfileIssuesDialog: FC<PIDProps> = ({ open, onClose }) => {
  // State
  const snackpatch = useAppDispatchWithSnackbar();
  const errorsSelector = useAppSelector(selectProfileBuilderFormResponseErrors);
  const lensId = useAppSelector(selectLensEditorLensId);
  const lensEditorFormFields = useAppSelector(selectLensEditorFormFields);
  const validationLoading = useAppSelector(selectProfileBuilderFormResponseErrorsLoading);
  const [isCopied, copy] = useCopy();
  const [filteredFieldsId, setFilteredFieldsId] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const formResponseErrorFields = useAppSelector(selectProfileBuilderFormResponseErrorFieldsUnique);

  // I want to run the following after the snackpatch is done
  // but I can not get the updated selector data in time, im not sure how to do this but this works for now
  useEffect(() => {
    if (validationLoading === RequestStatus.SUCCEEDED && errorsSelector.length === 0) {
      setShowConfetti(true);
    }
  }, [errorsSelector]);

  // Event handlers
  const handleValidateClick = (e: SyntheticEvent) => {
    e.preventDefault();
    void snackpatch(fetchFormResponseErrors({ lensId }), {
      successMessage: "Successfully retrieved profile issues",
      errorMessage: "Unable to verify profile",
    });
  };

  const handleFieldClick = (fieldId: number) => {
    setFilteredFieldsId(fieldId);
  };

  // The following feels wrong, but I'm not sure how to do it better

  const filteredErrors = useMemo(() => {
    if (filteredFieldsId) {
      return errorsSelector.filter((e) => e.fieldId === filteredFieldsId);
    }
    return errorsSelector;
  }, [errorsSelector, filteredFieldsId]);

  // Render
  return (
    <>
      {showConfetti && <PatternConfetti />}
      <Dialog open={open} onClose={onClose}>
        <Box
          sx={{
            padding: 2,
            width: 500,
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 1,
            }}>
            <Box>
              <P variant="h5">Profile Issues ({errorsSelector?.length})</P>
            </Box>
            <Box>
              <Tooltip title={isCopied ? "Copied!" : "Copy All to Clipboard"}>
                <Button
                  color="default"
                  onClick={() => {
                    copy(JSON.stringify(errorsSelector, null, 2));
                  }}>
                  {isCopied ? <DoneAllIcon /> : <CopyAllIcon />}
                </Button>
              </Tooltip>
              <Tooltip title="Refresh Issues">
                <LoadingButton
                  color="default"
                  onClick={handleValidateClick}
                  loading={validationLoading === RequestStatus.PENDING}>
                  {validationLoading === RequestStatus.PENDING ? null : <RefreshIcon color="inherit" />}
                </LoadingButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              marginBottom: 1,
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              // Basically tailwind space-x-1
              "& > * + *": {
                marginLeft: 1,
              },
            }}>
            {formResponseErrorFields.map((id, i) => {
              const fieldName = lensEditorFormFields.find((field) => field.id === id)?.name;
              return (
                <Box key={i + "-field-filter-chip"}>
                  <Chip
                    size="small"
                    label={`${fieldName} (${id})`}
                    deleteIcon={filteredFieldsId === id || formResponseErrorFields.length === 1 ? <DoneIcon /> : <></>}
                    onDelete={() => {
                      // Do nothing, this is just to show the checkmark
                    }}
                    onClick={() => {
                      handleFieldClick(id);
                    }}
                  />
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              height: 400,
              overflow: "auto",
              // Basically tailwind space-y-1
              "& > * + *": {
                marginTop: 1,
              },
            }}>
            {validationLoading !== RequestStatus.SUCCEEDED && errorsSelector.length === 0 ? (
              [1, 2, 3].map((i) => {
                return (
                  <Skeleton
                    variant="rectangular"
                    height={80}
                    key={"loading-skele-mui-" + i}
                    sx={{
                      borderRadius: 1,
                    }}
                  />
                );
              })
            ) : filteredErrors.length > 0 ? (
              filteredErrors.map((issue, i) => {
                return <IssueCard key={i + "-issue-card"} issue={issue} />;
              })
            ) : (
              <>
                <Alert severity="success">No Issues found with this Profile.</Alert>
              </>
            )}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ProfileIssuesDialog;
